// ============================================================================
// 📊 MANPOWER SITE DASHBOARD - ATTENDANCE BY SITE
// Shows attendance grouped by site location instead of project code
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { ManpowerEmployee } from '../../../../services/manpower.service';
import { manpowerAttendanceService, AttendanceRecord } from '../../../../services/manpowerAttendance.service';
import styles from './ManpowerProjectCodeDashboard.module.css'; // Reuse same styles

interface ManpowerSiteDashboardProps {
  employees: ManpowerEmployee[];
  projectName: string;
  onFilterChange: (site: string | null) => void;
}

interface SiteStats {
  site: string;
  totalAssigned: number;
  presentToday: number;
  absentToday: number;
  attendanceRate: number;
}

type DateFilterType = 'today' | 'yesterday' | 'custom';

const ManpowerSiteDashboard: React.FC<ManpowerSiteDashboardProps> = ({
  employees,
  projectName,
  onFilterChange
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [attendanceData, setAttendanceData] = useState<Map<string, AttendanceRecord>>(new Map());
  const [showSiteList, setShowSiteList] = useState(false);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState<DateFilterType>('today');
  const [customDate, setCustomDate] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Get UNIQUE employees (deduplicated by empNumber)
  const uniqueEmployees = useMemo(() => {
    const seen = new Map<string, ManpowerEmployee>();
    employees.forEach(emp => {
      if (!seen.has(emp.empNumber)) {
        seen.set(emp.empNumber, emp);
      }
    });
    return Array.from(seen.values());
  }, [employees]);

  // Calculate date strings
  const todayDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const yesterdayDate = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }, []);

  // Handle date filter change
  const handleDateFilterChange = (filter: DateFilterType, date?: string) => {
    setDateFilter(filter);
    
    let newDate: string;
    switch (filter) {
      case 'today':
        newDate = todayDate;
        break;
      case 'yesterday':
        newDate = yesterdayDate;
        break;
      case 'custom':
        newDate = date || customDate || todayDate;
        setCustomDate(newDate);
        break;
      default:
        newDate = todayDate;
    }
    
    setSelectedDate(newDate);
    console.log(`📅 Site Dashboard - Date filter changed to: ${filter} (${newDate})`);
  };

  // Format date display
  const formatDateDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Load attendance data
  useEffect(() => {
    const loadData = async () => {
      if (uniqueEmployees.length === 0) return;
      
      setLoading(true);
      try {
        const employeeList = uniqueEmployees.map(emp => ({
          empCode: emp.empNumber,
          employeeName: emp.name
        }));

        console.log(`📥 Loading site attendance for ${selectedDate}...`);
        const attendance = await manpowerAttendanceService.getAttendanceForDate(
          employeeList,
          selectedDate
        );
        
        setAttendanceData(attendance);
        console.log(`✅ Loaded site attendance for ${selectedDate}`);
      } catch (error) {
        console.error('Error loading site attendance:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [uniqueEmployees, selectedDate]);

  // Calculate TOTALS using unique employees only
  const totals = useMemo(() => {
    const total = uniqueEmployees.length;
    let present = 0;
    
    uniqueEmployees.forEach(emp => {
      const attendance = attendanceData.get(emp.empNumber);
      if (attendance && ['Present', 'Late', 'Early Leave', 'Incomplete'].includes(attendance.status)) {
        present++;
      }
    });

    const absent = total - present;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;
    
    return { total, present, absent, rate };
  }, [uniqueEmployees, attendanceData]);

  // Calculate statistics by SITE
  const siteStats = useMemo(() => {
    const statsMap = new Map<string, SiteStats>();
  
    employees.forEach(emp => {
      const site = emp.site || 'Unknown Site';
      
      if (!statsMap.has(site)) {
        statsMap.set(site, {
          site: site,
          totalAssigned: 0,
          presentToday: 0,
          absentToday: 0,
          attendanceRate: 0
        });
      }
  
      const stats = statsMap.get(site)!;
      stats.totalAssigned++;
  
      const attendance = attendanceData.get(emp.empNumber);
      const hasCheckedIn = attendance && ['Present', 'Late', 'Early Leave', 'Incomplete'].includes(attendance.status);
      
      if (hasCheckedIn) {
        stats.presentToday++;
      } else {
        stats.absentToday++;
      }
  
      stats.attendanceRate = stats.totalAssigned > 0 
        ? Math.round((stats.presentToday / stats.totalAssigned) * 100)
        : 0;
    });
  
    return Array.from(statsMap.values()).sort((a, b) => b.totalAssigned - a.totalAssigned);
  }, [employees, attendanceData]);

  // Get max value for bar scaling
  const maxAssigned = useMemo(() => {
    return Math.max(...siteStats.map(s => s.totalAssigned), 1);
  }, [siteStats]);

  // Handle site selection
  const handleSiteSelect = (site: string) => {
    if (selectedSite === site) {
      setSelectedSite(null);
      onFilterChange(null);
    } else {
      setSelectedSite(site);
      onFilterChange(site);
    }
    setShowSiteList(false);
  };

  return (
    <div className={styles.dashboard}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Site Location Dashboard</h2>
          <p className={styles.subtitle}>
            {projectName} - {formatDateDisplay(selectedDate)}
          </p>
        </div>
      </div>

      {/* Date Filter Buttons */}
      <div className={styles.dateFilterSection}>
        <div className={styles.dateFilterButtons}>
          <button
            className={`${styles.dateFilterButton} ${dateFilter === 'today' ? styles.active : ''}`}
            onClick={() => handleDateFilterChange('today')}
          >
            <svg className={styles.dateIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Today
          </button>

          <button
            className={`${styles.dateFilterButton} ${dateFilter === 'yesterday' ? styles.active : ''}`}
            onClick={() => handleDateFilterChange('yesterday')}
          >
            <svg className={styles.dateIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Yesterday
          </button>

          <div className={styles.customDateWrapper}>
            <button
              className={`${styles.dateFilterButton} ${dateFilter === 'custom' ? styles.active : ''}`}
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <svg className={styles.dateIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
              </svg>
              Custom Date
            </button>

            {showDatePicker && (
              <div className={styles.datePickerDropdown}>
                <input
                  type="date"
                  value={customDate || selectedDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    handleDateFilterChange('custom', newDate);
                  }}
                  max={todayDate}
                  className={styles.dateInput}
                />
                <button
                  onClick={() => setShowDatePicker(false)}
                  className={styles.datePickerClose}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>

        {dateFilter !== 'today' && (
          <div className={styles.dateIndicator}>
            Showing data for: <strong>{formatDateDisplay(selectedDate)}</strong>
          </div>
        )}
      </div>

      {/* Compact Summary Row */}
      <div className={styles.compactSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{totals.total}</span>
          <span className={styles.summaryLabel}>Total</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={`${styles.summaryItem} ${styles.success}`}>
          <span className={styles.summaryValue}>{totals.present}</span>
          <span className={styles.summaryLabel}>Present</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={`${styles.summaryItem} ${styles.warning}`}>
          <span className={styles.summaryValue}>{totals.absent}</span>
          <span className={styles.summaryLabel}>Absent</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={`${styles.summaryItem} ${styles.info}`}>
          <span className={styles.summaryValue}>{totals.rate}%</span>
          <span className={styles.summaryLabel}>Rate</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{siteStats.length}</span>
          <span className={styles.summaryLabel}>Sites</span>
        </div>
      </div>

      {/* Site Dropdown & Filter Status */}
      <div className={styles.filterSection}>
        <div className={styles.dropdownContainer}>
          <button 
            className={styles.dropdownButton}
            onClick={() => setShowSiteList(!showSiteList)}
          >
            <span>
              {selectedSite 
                ? `Site: ${selectedSite}` 
                : `All Sites (${siteStats.length})`}
            </span>
            <svg 
              className={`${styles.dropdownIcon} ${showSiteList ? styles.rotated : ''}`} 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          {showSiteList && (
            <div className={styles.dropdownList}>
              <div 
                className={`${styles.dropdownItem} ${!selectedSite ? styles.active : ''}`}
                onClick={() => {
                  setSelectedSite(null);
                  onFilterChange(null);
                  setShowSiteList(false);
                }}
              >
                <span>All Sites</span>
                <span className={styles.itemCount}>{siteStats.length}</span>
              </div>
              <div className={styles.dropdownDivider} />
              {siteStats.map(stat => (
                <div 
                  key={stat.site}
                  className={`${styles.dropdownItem} ${selectedSite === stat.site ? styles.active : ''}`}
                  onClick={() => handleSiteSelect(stat.site)}
                >
                  <span>{stat.site}</span>
                  <div className={styles.itemStats}>
                    <span className={styles.presentCount}>{stat.presentToday}</span>
                    <span className={styles.statSeparator}>/</span>
                    <span className={styles.totalCount}>{stat.totalAssigned}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedSite && (
          <button 
            className={styles.clearFilter}
            onClick={() => {
              setSelectedSite(null);
              onFilterChange(null);
            }}
          >
            Clear Filter ×
          </button>
        )}
      </div>

      {/* Site Bar Graphs */}
      <div className={styles.barGraphSection}>
        <h3 className={styles.sectionTitle}>Attendance by Site Location</h3>
        
        <div className={styles.barGraphContainer}>
          {siteStats.map(stat => {
            const presentWidth = (stat.presentToday / maxAssigned) * 100;
            const absentWidth = (stat.absentToday / maxAssigned) * 100;
            const isSelected = selectedSite === stat.site;
            
            return (
              <div 
                key={stat.site}
                className={`${styles.barRow} ${isSelected ? styles.selectedRow : ''}`}
                onClick={() => handleSiteSelect(stat.site)}
              >
                <div className={styles.barLabel}>
                  <span className={styles.projectCode}>{stat.site}</span>
                  <span className={styles.assignedCount}>{stat.totalAssigned}</span>
                </div>
                
                <div className={styles.barWrapper}>
                  <div className={styles.barTrack}>
                    <div 
                      className={styles.barPresent}
                      style={{ width: `${presentWidth}%` }}
                    />
                    <div 
                      className={styles.barAbsent}
                      style={{ width: `${absentWidth}%`, left: `${presentWidth}%` }}
                    />
                  </div>
                </div>
                
                <div className={styles.barStats}>
                  <span className={styles.presentStat}>{stat.presentToday}</span>
                  <span className={styles.absentStat}>{stat.absentToday}</span>
                  <span className={styles.rateStat}>{stat.attendanceRate}%</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.presentDot}`} />
            <span>Checked In</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.absentDot}`} />
            <span>Not Checked In</span>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p>Loading attendance data...</p>
        </div>
      )}
    </div>
  );
};

export default ManpowerSiteDashboard;